import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Users,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Download,
  Mail,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const SYNC_STATUS_CONFIG = {
  created: { label: 'Created', color: 'bg-green-500', icon: CheckCircle },
  updated: { label: 'Updated', color: 'bg-blue-500', icon: CheckCircle },
  existing: { label: 'Existing', color: 'bg-slate-500', icon: CheckCircle },
  error: { label: 'Error', color: 'bg-red-500', icon: XCircle },
  skipped: { label: 'Skipped', color: 'bg-amber-500', icon: AlertCircle },
  pending: { label: 'Pending', color: 'bg-slate-400', icon: Clock },
};

const EventRegistrations = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useCMSAuth();
  
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchEvent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/content/events/${eventId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEvent(data);
      }
    } catch (e) {
      console.error('Failed to fetch event:', e);
    }
  }, [eventId, token]);

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/cms/content/event-registrations?event_id=${eventId}&page=${page}&limit=20`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setRegistrations(data.registrations || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch registrations:', e);
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  }, [eventId, page, token]);

  useEffect(() => {
    fetchEvent();
    fetchRegistrations();
  }, [fetchEvent, fetchRegistrations]);

  const exportCSV = () => {
    if (registrations.length === 0) {
      toast.error('No registrations to export');
      return;
    }
    
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Registered At', 'Zoho Status', 'Zoho Lead ID'];
    const rows = registrations.map(r => [
      r.first_name,
      r.last_name,
      r.email,
      r.phone,
      r.registered_at ? new Date(r.registered_at).toLocaleString() : '',
      r.zoho_sync_status || 'pending',
      r.zoho_lead_id || ''
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c || ''}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event?.title || 'event'}_registrations.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  const filteredRegistrations = registrations.filter(r => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      r.first_name?.toLowerCase().includes(term) ||
      r.last_name?.toLowerCase().includes(term) ||
      r.email?.toLowerCase().includes(term) ||
      r.phone?.includes(term)
    );
  });

  const getSyncStatusBadge = (registration) => {
    const status = registration.zoho_sync_status || (registration.zoho_synced ? 'created' : 'pending');
    const config = SYNC_STATUS_CONFIG[status] || SYNC_STATUS_CONFIG.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} text-white text-xs`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const retrySync = async (registrationId) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/content/event-registrations/${registrationId}/retry-sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Sync retry initiated');
        // Refresh after a short delay to see the result
        setTimeout(fetchRegistrations, 2000);
      } else {
        toast.error('Failed to retry sync');
      }
    } catch (e) {
      toast.error('Network error');
    }
  };

  return (
    <AdminLayout title="Event Registrations">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/events')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Events
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchRegistrations}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Event Info */}
        {event && (
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">{event.title}</h2>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-600 dark:text-slate-400">
              <span>{event.event_date && new Date(event.event_date).toLocaleDateString()}</span>
              <span>•</span>
              <span>{event.location || 'Online'}</span>
              {event.trade_show_name && (
                <>
                  <span>•</span>
                  <Badge variant="outline">Trade Show: {event.trade_show_name}</Badge>
                </>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white">{total}</p>
                <p className="text-xs text-slate-500">Total Registrations</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white">
                  {registrations.filter(r => r.zoho_synced).length}
                </p>
                <p className="text-xs text-slate-500">Synced to Zoho</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white">
                  {registrations.filter(r => !r.zoho_synced && !r.zoho_sync_status).length}
                </p>
                <p className="text-xs text-slate-500">Pending Sync</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800 dark:text-white">
                  {registrations.filter(r => r.zoho_sync_status === 'error').length}
                </p>
                <p className="text-xs text-slate-500">Sync Errors</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Registered At</TableHead>
                <TableHead>Zoho Status</TableHead>
                <TableHead>Zoho Lead ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    <p className="text-sm text-slate-500 mt-2">Loading registrations...</p>
                  </TableCell>
                </TableRow>
              ) : filteredRegistrations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Users className="w-8 h-8 mx-auto text-slate-300" />
                    <p className="text-sm text-slate-500 mt-2">
                      {searchTerm ? 'No registrations match your search' : 'No registrations yet'}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRegistrations.map((reg) => (
                  <TableRow key={reg.id}>
                    <TableCell>
                      <div className="font-medium text-slate-800 dark:text-white">
                        {reg.first_name} {reg.last_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                          <Mail className="w-3 h-3" />
                          {reg.email}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                          <Phone className="w-3 h-3" />
                          {reg.phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 dark:text-slate-400">
                      {reg.registered_at ? new Date(reg.registered_at).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {getSyncStatusBadge(reg)}
                          {(!reg.zoho_synced || reg.zoho_sync_status === 'error' || reg.zoho_sync_status === 'pending') && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2 text-xs"
                              onClick={() => retrySync(reg.id)}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Retry
                            </Button>
                          )}
                        </div>
                        {reg.zoho_sync_message && (
                          <p className="text-xs text-slate-500 max-w-[200px] truncate" title={reg.zoho_sync_message}>
                            {reg.zoho_sync_message}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-mono text-slate-500">
                      {reg.zoho_lead_id || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default EventRegistrations;
